/**
 * Service for validating form-related fields
 *
 * IMPORTANT: Before adding a rule, check the validator library to ensure
 * the rule does not already exist here: https://github.com/chriso/validator.js
 */
import validator from 'validator';

/**
 * Determines whether or not a string contains a minimum number of chars
 *
 * @param {string} length - Minimum number of characters to accept
 * @param {string} value - String to test
 * @returns {boolean} - Whether or not string has the minimum number of chars
 */
validator.hasMinLength = (minLength) => (value) => Boolean(!value || value.length >= minLength);

/**
 * Determines whether or not a string contains a maximum number of chars
 *
 * @param {string} length - Maximum number of characters to accept
 * @param {string} value - String to test
 * @returns {boolean} - Whether or not string has the maximum number of chars
 */
validator.hasMaxLength = (maxLength) => (value) => Boolean(!value || value.length <= maxLength);

/**
 * Determines whether or not a value is defined or not
 *
 * @param {string} value - String to test
 * @returns {boolean} - Whether or not string has has a value
 */
validator.isRequired = (value) => Boolean(value);

/**
 * Determines whether or not a value is contains only alphanumeric
 * and underscore characters
 *
 * @param {string} value - String to test
 * @returns {boolean} - Whether or not string has has a value
 */
validator.isAlphanumericUnderscore = (value) => RegExp('^[A-Za-z0-9_]+$').test(value);

/**
 * Determines whether or not a value is contains is a valid
 * IPv4 address or range
 *
 * @param {string} value - String to test
 * @returns {boolean} - Whether or not string has has a value
 */
validator.isCidr = (value) => {
  const ipv4Regex = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
  const cidrv4Regex = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/([0-9]|[1-2][0-9]|3[0-2]))$/;
  return cidrv4Regex.test(value) || ipv4Regex.test(value);
};

validator.isAclUnique = (newAcl, acls) =>
  acls.filter((acl) => acl.kind + acl.cidr === newAcl.kind + newAcl.cidr).length === 0;

validator.isUserUnique = (newEmail, users, invites) =>
  users.filter((user) => user.email.toLowerCase() === newEmail.toLowerCase()).length === 0 &&
  invites.filter((invite) => invite.email2invite.toLowerCase() === newEmail.toLowerCase())
    .length === 0;

export default validator;
